// Панели показателей: укомплектованность и сводка по ведомостям.
//
// Проценты приходят с сервера уже посчитанными и здесь не пересчитываются: два места, где
// делят одни и те же числа, однажды покажут разную укомплектованность одного объекта.

import type { FileResponse } from '@/shared/api/client';
import { request, requestFile } from '@/shared/api/client';
import { queryString, type QueryValue } from '@/shared/api/query';

export interface SiteStaffing {
  site_id: string;
  site_name: string;
  customer_name?: string | null;
  city?: string | null;
  legal_entity_id: string;
  legal_entity_name: string;
  required: number;
  assigned: number;
  deficit: number;
  /** null — активных постов нет: делить не на что, и ноль тут означал бы неправду. */
  percent?: number | null;
  /**
   * Норматив, факт и недобор В ЧАСАХ за выбранный месяц, строками.
   *
   * Тз 5.7 про укомплектованность: «источник факта — табель». Головами считается, сколько
   * людей стоит на постах СЕГОДНЯ, а это другой вопрос: назначенный может не выйти. Пусто —
   * месяц не запрашивали либо табеля за него нет, и это НЕ ноль.
   */
  norm_hours?: string | null;
  fact_hours?: string | null;
  deficit_hours?: string | null;
}

/** Итог по заказчику либо по юрлицу. Тз 5.7 требует оба разреза дословно. */
export interface StaffingGroup {
  key: string;
  title: string;
  sites: number;
  required: number;
  assigned: number;
  deficit: number;
  norm_hours?: string | null;
  fact_hours?: string | null;
  deficit_hours?: string | null;
}

export interface StaffingOut {
  items: SiteStaffing[];
  total_required: number;
  total_assigned: number;
  total_deficit: number;
  understaffed_sites: number;
  /** Итоги по разрезам считает СЕРВЕР: сложенное на экране разойдётся с общим итогом. */
  by_customer?: StaffingGroup[];
  by_legal_entity?: StaffingGroup[];
  total_norm_hours?: string | null;
  total_fact_hours?: string | null;
  total_deficit_hours?: string | null;
  /**
   * «Должно минус вышло» по объектам с известными часами; минус — переработка. То же число, что
   * «Недобор в часах» на панели показателей. Необязательное: старый ответ его не содержит.
   */
  total_net_hours?: string | null;
  generated_at: string;
}

export interface SitePayroll {
  payroll_id: string;
  site_id?: string | null;
  /** Пусто — административная ведомость: у неё объекта нет по устройству. */
  site_name?: string | null;
  legal_entity_id: string;
  legal_entity_name: string;
  status: string;
  /** Состояние по-русски: словарь названий живёт в контракте, а не на экране. */
  status_title: string;
  employees: number;
  /** Часы строкой: дробное по сети не передаётся числом. */
  hours_fact: string;
  /**
   * Считался ли расчёт хоть по одному человеку. Ноль по непосчитанной ведомости — это
   * «не знаем», а не «объект стоит ноль». Поле необязательное: старый ответ его не содержит.
   */
  calculated?: boolean;
  /**
   * У скольких людей суммы неизвестны: расчёт не проводился, не заполнен тариф либо нет часов.
   * «Посчитана хоть по одному» — не «посчитана». Необязательное: старый ответ его не содержит.
   */
  unknown_employees?: number;
  accrued: number;
  official_total: number;
  /** Сколько аванса просили удержать. */
  advance_total: number;
  /** Сколько удержали на самом деле: остаток мог уйти в долг (сценарий 31). */
  advance_withheld: number;
  /** Форма, билеты, обучение, штрафы, кредит — всё, что каскад взял после аванса. */
  deductions_total: number;
  total_payout: number;
}

export interface PayrollSummaryOut {
  period_year: number;
  period_month: number;
  items: SitePayroll[];
  total_employees: number;
  total_accrued: number;
  total_official: number;
  total_advance: number;
  total_advance_withheld: number;
  total_deductions: number;
  total_payout: number;
  /** Людей месяца с неизвестными суммами. Больше нуля — итоги выше неполные. */
  total_unknown_employees?: number;
  generated_at: string;
}

/**
 * Отбор отчёта. Тз, раздел 5.7: «Отчёты параметризованы (период, юридическое лицо, заказчик,
 * объект)». Период у каждого отчёта свой (у укомплектованности его нет вовсе), а эти три —
 * общие, и живут они одним типом: три копии одной тройки разъезжаются на первой же правке.
 */
export interface ReportScope {
  legal_entity_id?: string;
  customer_id?: string;
  site_id?: string;
}

export function fetchStaffing(
  params: ReportScope & { year?: number; month?: number } = {},
): Promise<StaffingOut> {
  return request<StaffingOut>(`/api/v1/reports/staffing${queryString({ ...params })}`);
}

export function fetchPayrollSummary(
  params: ReportScope & { year: number; month: number },
): Promise<PayrollSummaryOut> {
  return request<PayrollSummaryOut>(`/api/v1/reports/payroll-summary${queryString({ ...params })}`);
}

/** Один вид удержания за месяц: аванс, билеты, форма, обучение. */
export interface DeductionKindOut {
  /** Код колонки ведомости: advance, tickets, uniform, training. */
  code: string;
  /** Название колонки из каталога: «Аванс», «Билеты». */
  title: string;
  /** Удержано у всех, копейки. */
  total: number;
  people: number;
}

export interface DeductionPersonOut {
  employee_id: string;
  employee_name: string;
  /** Где удержано: объекты ведомостей человека за месяц. */
  sites: string[];
  /** Код колонки → удержано, копейки. Только не ноль. */
  amounts: Record<string, number>;
  total: number;
}

/**
 * Кому сколько удержали за месяц. Числа — из тех же сумм, что стоят в колонках ведомостей
 * месяца, поэтому сходятся с ними до рубля.
 */
export interface DeductionsReportOut {
  period_year: number;
  period_month: number;
  kinds: DeductionKindOut[];
  items: DeductionPersonOut[];
  total: number;
  /** Ведомостей за месяц в видимости. Ноль — «ведомостей нет», а не «ничего не удержано». */
  sheets: number;
  /** Из них посчитаны хотя бы по одному человеку. */
  sheets_calculated: number;
  generated_at: string;
}

export function fetchDeductions(params: {
  year: number;
  month: number;
  legal_entity_id?: string;
}): Promise<DeductionsReportOut> {
  return request<DeductionsReportOut>(`/api/v1/reports/deductions${queryString({ ...params })}`);
}

/** Выгрузка укомплектованности. Файл собирает сервер по той же выборке, что на экране. */
export function exportStaffing(params: ReportScope = {}): Promise<FileResponse> {
  return requestFile(
    `/api/v1/reports/staffing/export${queryString({ ...params })}`,
    {},
    'Укомплектованность.xlsx',
  );
}

export function exportPayrollSummary(
  params: ReportScope & { year: number; month: number },
): Promise<FileResponse> {
  return requestFile(
    `/api/v1/reports/payroll-summary/export${queryString({ ...params })}`,
    {},
    `Сводка ${String(params.month).padStart(2, '0')}.${params.year}.xlsx`,
  );
}

// ── Долги сотрудников (сценарий 59) ────────────────────────────────────────────────────────

export interface DebtTypeRow {
  debt_type: string;
  /** Название типа приходит с сервера: словарь названий живёт в контракте, а не на экране. */
  title: string;
  people: number;
  debts: number;
  initial_total: number;
  outstanding_total: number;
  repaid_in_period: number;
}

export interface LeavingDebtor {
  employee_id: string;
  employee_name: string;
  debts: number;
  outstanding: number;
}

export interface DebtsReportOut {
  period_from: string;
  period_to: string;
  items: DebtTypeRow[];
  total_outstanding: number;
  total_repaid: number;
  leaving_with_debt: LeavingDebtor[];
  generated_at: string;
}

export function fetchDebtsReport(params: {
  year: number;
  month: number;
  legal_entity_id?: string;
}): Promise<DebtsReportOut> {
  return request<DebtsReportOut>(`/api/v1/reports/debts${queryString(params)}`);
}

/** Выгрузка реестра долгов: два листа — итоги по типам и уволенные с непогашенным долгом. */
export function exportDebts(params: {
  year: number;
  month: number;
  legal_entity_id?: string;
}): Promise<FileResponse> {
  return requestFile(
    `/api/v1/reports/debts/export${queryString(params)}`,
    {},
    `Реестр долгов ${String(params.month).padStart(2, '0')}.${params.year}.xlsx`,
  );
}

// ── Реестр выплат за период (тз, раздел 5.7) ───────────────────────────────────────────────

export interface PayoutRegisterRow {
  employee_id: string;
  employee_name: string;
  legal_entity_id: string;
  legal_entity_name: string;
  /** Сколько к выплате всего, копейки. Сложено по всем ведомостям человека в периоде. */
  total_payout: number;
  /** Куда переводить: последние четыре цифры карты либо телефона. Полного номера здесь нет. */
  payout_last4?: string | null;
  payout_method?: string | null;
  /** Отмечена ли выплата: по признаку видно, кому ещё не перевели. */
  is_paid: boolean;
}

export interface PayoutRegisterOut {
  period_year: number;
  period_month: number;
  items: PayoutRegisterRow[];
  total_employees: number;
  total_payout: number;
  paid_employees: number;
  paid_total: number;
  generated_at: string;
}

export function fetchPayoutRegister(
  params: ReportScope & { year: number; month: number },
): Promise<PayoutRegisterOut> {
  return request<PayoutRegisterOut>(`/api/v1/reports/payouts${queryString({ ...params })}`);
}

/** Выгрузка реестра выплат: плоский список для ручной передачи в банк-клиент. */
export function exportPayoutRegister(
  params: ReportScope & { year: number; month: number },
): Promise<FileResponse> {
  return requestFile(
    `/api/v1/reports/payouts/export${queryString({ ...params })}`,
    {},
    `Реестр выплат ${String(params.month).padStart(2, '0')}.${params.year}.xlsx`,
  );
}

// ── Движение денег в полях (сценарий 62) ───────────────────────────────────────────────────

export interface CategoryFlowRow {
  category_id: string;
  category_code: string;
  category_title: string;
  claims: number;
  amount: number;
  /** Доля посчитана сервером: делить те же числа второй раз значит завести второй ответ. */
  percent: number;
}

export interface CashFlowReportOut {
  period_from: string;
  period_to: string;
  items: CategoryFlowRow[];
  total_amount: number;
  total_claims: number;
  /**
   * Сверка месяца: выдали, вернули, осталось. Считает сервер, экран ничего не вычитает.
   * Необязательные: пусты у тех, кому касса не выдаёт (старший объекта), и у старого сервера.
   */
  issued_total?: number | null;
  /** Сколько выдач вошло в `issued_total`. */
  issued_count?: number | null;
  /** Пусто при отборе по юрлицу: у возврата нет юрлица, и они в сверку не входят. */
  returned_total?: number | null;
  /** Осталось от выданного за месяц. Меньше нуля — потрачено сверх выданного. */
  left_total?: number | null;
  generated_at: string;
}

/** Параметры отчёта расхода за месяц. */
export interface CashFlowQuery {
  [key: string]: QueryValue | readonly QueryValue[] | undefined;
  year: number;
  month: number;
  legal_entity_id?: string;
  accountable_person_id?: string;
  /**
   * Месяц не задан человеком: если запрошенный пуст, сервер отвечает последним более ранним,
   * где что-то было. Какой месяц пришёл, видно по `period_from` в ответе. Месяц, выбранный в
   * адресе, флагом не сопровождается никогда: явный выбор главнее подсказки.
   */
  latest_if_empty?: boolean;
}

export function fetchCashFlow(params: CashFlowQuery): Promise<CashFlowReportOut> {
  return request<CashFlowReportOut>(`/api/v1/reports/cash-flow${queryString(params)}`);
}

export function exportCashFlow(params: {
  year: number;
  month: number;
  legal_entity_id?: string;
  accountable_person_id?: string;
}): Promise<FileResponse> {
  return requestFile(`/api/v1/reports/cash-flow/export${queryString(params)}`);
}

// ── Ряды во времени ────────────────────────────────────────────────────────────────────────
//
// До 08.09.2026 в кабинете не было ни одного числа во времени: каждый отчёт показывал ровно
// один выбранный месяц. Ряды отвечают на единственный вопрос, которого не было, — «растёт
// или падает».

export interface PayrollMonthRow {
  period_year: number;
  period_month: number;
  /** Короткая подпись столбца: «сен 26». Собрана сервером — календарь в продукте один. */
  label: string;
  employees: number;
  /** Начислено. Три следующих числа складываются в него ровно. */
  accrued: number;
  official_total: number;
  /** Удержанный аванс и всё, что каскад взял после него, одной величиной. */
  deductions_total: number;
  total_payout: number;
}

export interface PayrollTrendOut {
  items: PayrollMonthRow[];
  /** Сколько месяцев спрашивали. Пришло меньше — за остальные ведомостей нет вовсе. */
  months: number;
  generated_at: string;
}

export function fetchPayrollTrend(params: {
  year: number;
  month: number;
  months?: number;
  legal_entity_id?: string;
  customer_id?: string;
  site_id?: string;
}): Promise<PayrollTrendOut> {
  return request<PayrollTrendOut>(`/api/v1/reports/payroll-trend${queryString(params)}`);
}

export interface CashDayRow {
  day: string;
  issued: number;
  returned: number;
  spent: number;
}

export interface CashTrendOut {
  period_from: string;
  period_to: string;
  /** Дни идут подряд и без пропусков: день без движения приходит нулями. */
  items: CashDayRow[];
  total_issued: number;
  total_returned: number;
  total_spent: number;
  generated_at: string;
}

export function fetchCashTrend(params: {
  days?: number;
  legal_entity_id?: string;
  accountable_person_id?: string;
}): Promise<CashTrendOut> {
  return request<CashTrendOut>(`/api/v1/reports/cash-trend${queryString(params)}`);
}

/**
 * Акт сверки по подотчёту.
 *
 * Без `accountable_person_id` — общий: тот же список, что на пульте, с местом под подпись. По
 * нему сверяют всех разом перед закрытием месяца.
 *
 * С опознавателем лица — ЛИЧНЫЙ акт: имя в шапке, его операции с датами, его итог, его
 * подпись. Тз 5.7 называет «кнопку выгрузки акта сверки по руководителю», и до 06.09.2026
 * такого акта не было: подписывать человеку предлагали лист с чужими фамилиями и остатками.
 */
export function exportReconciliation(
  params: { legal_entity_id?: string; accountable_person_id?: string } = {},
): Promise<FileResponse> {
  return requestFile(`/api/v1/reports/accountable/export${queryString(params)}`);
}
